import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { Gift, Sparkles, Rocket, ArrowRight, BadgePercent, Tag } from "lucide-react";

// Rotating promos — a different one shows on each visit.
const PROMOS = [
    {
        key: "iphone_contest",
        icon: Gift,
        eyebrow: "Limited-time giveaway",
        title: "Win a brand-new iPhone",
        body: "Enter our monthly draw — free to join. Drop your email for a chance to win the latest iPhone, on us.",
        capture: true,
        cta: "Enter to win",
    },
    {
        key: "first_month_free",
        icon: Sparkles,
        eyebrow: "New customers",
        title: "No contract. eSIM in minutes.",
        body: "Talk & text on a fast eSIM network from $19.99/mo. Keep your number, activate in minutes, cancel anytime.",
        capture: false,
        cta: "See plans",
        to: "/mobile",
    },
    {
        key: "referral_bonus",
        icon: Rocket,
        eyebrow: "Refer & earn",
        title: "Give $20, get $20",
        body: "Love FlexLine? Invite a friend — you both get $20 bill credit when they activate. It adds up fast.",
        capture: false,
        cta: "Start referring",
        to: "/referrals",
    },
    {
        key: "annual_discount",
        icon: BadgePercent,
        eyebrow: "Save more",
        title: "Save up to 20% yearly",
        body: "Switch to annual billing and pocket the savings — the same unlimited plan for less every month.",
        capture: false,
        cta: "See annual plans",
        to: "/mobile",
    },
    {
        key: "free_activation",
        icon: Tag,
        eyebrow: "No hidden fees",
        title: "Free eSIM & activation",
        body: "No SIM to buy, no activation fee, no contract. Scan a QR and you're live in minutes — keep your number free.",
        capture: false,
        cta: "Get started free",
        to: "/mobile",
    },
];

const IDX_KEY = "flexline_promo_idx";
const SEEN_KEY = "flexline_promo_seen_session";

export function PromoPopup() {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);
    const [promo, setPromo] = useState(PROMOS[0]);

    useEffect(() => {
        if (sessionStorage.getItem(SEEN_KEY)) return;
        const idx = Number(localStorage.getItem(IDX_KEY) || 0) % PROMOS.length;
        setPromo(PROMOS[idx]);
        localStorage.setItem(IDX_KEY, String((idx + 1) % PROMOS.length));
        const t = setTimeout(() => { setOpen(true); sessionStorage.setItem(SEEN_KEY, "1"); }, 4000);
        return () => clearTimeout(t);
    }, []);

    const enter = async () => {
        if (!email || !email.includes("@")) { toast.error("Please enter a valid email"); return; }
        setBusy(true);
        try {
            await api.post("/promo/enter", { email, promo_key: promo.key });
            toast.success("You're entered! Good luck 🍀");
            setOpen(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not enter — try again");
        }
        setBusy(false);
    };

    const go = () => { setOpen(false); navigate(promo.to || "/mobile"); };

    const Icon = promo.icon;
    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent className="max-w-md overflow-hidden p-0" data-testid="promo-popup">
                <div className="bg-[#4C1D95] text-white px-6 pt-6 pb-5">
                    <div className="w-11 h-11 rounded-sm bg-white/10 grid place-items-center mb-3">
                        <Icon className="w-6 h-6" />
                    </div>
                    <p className="font-mono text-[11px] uppercase tracking-widest text-[#C4B5FD]" data-testid="promo-eyebrow">{promo.eyebrow}</p>
                    <DialogTitle className="font-display text-3xl font-light mt-1 text-white" data-testid="promo-title">{promo.title}</DialogTitle>
                    <DialogDescription className="sr-only">{promo.body}</DialogDescription>
                </div>
                <div className="px-6 py-5">
                    <p className="text-sm text-zinc-600 leading-relaxed mb-4" data-testid="promo-body">{promo.body}</p>
                    {promo.capture ? (
                        <div className="flex gap-2">
                            <Input type="email" placeholder="you@email.com" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="promo-email" />
                            <Button onClick={enter} disabled={busy} data-testid="promo-enter-btn" className="bg-[#D97706] text-white rounded-sm shrink-0">
                                {busy ? "…" : promo.cta}
                            </Button>
                        </div>
                    ) : (
                        <Button onClick={go} data-testid="promo-cta-btn" className="w-full bg-[#D97706] text-white rounded-sm h-11">
                            {promo.cta} <ArrowRight className="w-4 h-4 ml-1" />
                        </Button>
                    )}
                    <button onClick={() => setOpen(false)} data-testid="promo-dismiss" className="mt-3 w-full text-center text-xs text-zinc-400 hover:text-zinc-600">No thanks</button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
